import { ApiError, formatCents, MAX_PAYOUT_DETAILS_LENGTH } from '@groundly/shared';
import { createFileRoute } from '@tanstack/react-router';
import { Check, Copy } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { useAffiliate, useClaimReferral, useUpdatePayoutDetails } from '../../../api/affiliate.js';
import { usePermissions } from '../../../api/me.js';
import styles from '../../../affiliate/affiliate.module.css';
import { Button } from '../../../components/Button.js';
import { DangerAlert } from '../../../components/DangerAlert.js';
import { InfoAlert } from '../../../components/InfoAlert.js';
import { PageHeading } from '../../../components/PageHeading.js';
import { LoadingArea } from '../../../components/PlaceholderPage.js';
import { PermissionDeniedPage } from '../../../components/SectionStates.js';
import { TextField } from '../../../components/TextField.js';
import { formatFullDateTime } from '../../../conversations/display.js';

/**
 * `/account/affiliate`: the account's referral link, what its referrals have
 * earned, and where Groundly sends payouts (`product/business-rules.md`,
 * Affiliates). Needs the billing and invoices permission.
 */
export const Route = createFileRoute('/_app/account/affiliate')({
  component: AffiliateRoute,
});

const TITLE = 'Affiliate';

function AffiliateRoute() {
  const permissions = usePermissions();
  if (!permissions.includes('billing_and_invoices')) {
    return <PermissionDeniedPage title={TITLE} permission="billing and invoices" />;
  }
  return <AffiliatePage />;
}

const shortDate = (iso: string) =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(
    new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso),
  );

function AffiliatePage() {
  const affiliate = useAffiliate();

  return (
    <>
      <PageHeading>{TITLE}</PageHeading>
      <p className={styles.caption}>Refer businesses to Groundly and earn from what they pay.</p>
      {affiliate.isError ? (
        <DangerAlert
          title="Your affiliate details didn't load"
          action={<Button onClick={() => void affiliate.refetch()}>Try again</Button>}
        >
          Groundly couldn&apos;t reach the server. Check your connection and try again.
        </DangerAlert>
      ) : affiliate.isPending ? (
        <LoadingArea />
      ) : (
        <div className={styles.stack}>
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Your referral link</h2>
            <p>
              Businesses that sign up with your link get{' '}
              <strong>
                {affiliate.data.referralDiscountPercent}% off their first{' '}
                {affiliate.data.referralDiscountCycles} invoices
              </strong>
              . You earn <strong>{affiliate.data.commissionPercent}%</strong> of every invoice they
              pay for their first {affiliate.data.commissionMonths} months.
            </p>
            <CopyLink link={affiliate.data.link} />
            <p className={styles.hint}>
              Your code is <span className={styles.code}>{affiliate.data.code}</span>. Someone who
              signed up without the link can enter it on this page within 30 days.
            </p>
          </section>

          <section className={styles.totals} aria-label="Totals">
            <Total label="Referred" value={String(affiliate.data.totals.referred)} />
            <Total label="Paying" value={String(affiliate.data.totals.paying)} />
            <Total label="Earned" value={formatCents(affiliate.data.totals.earnedCents)} />
            <Total label="Paid out" value={formatCents(affiliate.data.totals.paidOutCents)} />
            <Total label="Balance" value={formatCents(affiliate.data.totals.balanceCents)} strong />
          </section>

          <PayoutDetails current={affiliate.data.payoutDetails} />

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Commissions</h2>
            {affiliate.data.commissions.length === 0 ? (
              <p className={styles.muted}>
                Nothing yet. You earn once a business you referred pays an invoice.
              </p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Paid</th>
                    <th scope="col">From</th>
                    <th scope="col">Billing cycle</th>
                    <th scope="col" className={styles.num}>
                      Invoice
                    </th>
                    <th scope="col" className={styles.num}>
                      You earned
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {affiliate.data.commissions.map((c) => (
                    <tr key={c.invoiceNumber}>
                      <td>{shortDate(c.paidAt)}</td>
                      <td>{c.referralLabel}</td>
                      <td>
                        {shortDate(c.periodStart)} – {shortDate(c.periodEnd)}
                      </td>
                      <td className={styles.num}>{formatCents(c.invoiceTotalCents)}</td>
                      <td className={styles.num}>{formatCents(c.commissionCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {affiliate.data.referrals.length > 0 ? (
              <ul className={styles.referrals}>
                {affiliate.data.referrals.map((r) => (
                  <li key={r.id}>
                    <strong>{r.label}</strong> joined {shortDate(r.referredAt)} · {r.paidInvoices}{' '}
                    paid {r.paidInvoices === 1 ? 'invoice' : 'invoices'} · earns until{' '}
                    {shortDate(r.commissionUntil)}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          {affiliate.data.payouts.length > 0 ? (
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Payouts</h2>
              <ul className={styles.referrals}>
                {affiliate.data.payouts.map((p) => (
                  <li key={p.id}>
                    <strong>{formatCents(p.amountCents)}</strong> on{' '}
                    {formatFullDateTime(p.createdAt)}
                    {p.reference !== null ? ` · ${p.reference}` : ''}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {affiliate.data.referredBy.canClaim ? <ClaimCode /> : null}
        </div>
      )}
    </>
  );
}

function Total({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={styles.total}>
      <span className={styles.totalLabel}>{label}</span>
      <span className={strong ? styles.totalStrong : styles.totalValue}>{value}</span>
    </div>
  );
}

function CopyLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={styles.linkRow}>
      <input className={styles.linkInput} value={link} readOnly aria-label="Your referral link" />
      <Button
        onClick={() => {
          void navigator.clipboard.writeText(link).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        {copied ? (
          <Check aria-hidden="true" width={16} height={16} />
        ) : (
          <Copy aria-hidden="true" width={16} height={16} />
        )}
        {copied ? 'Copied' : 'Copy link'}
      </Button>
    </div>
  );
}

function PayoutDetails({ current }: { current: string | null }) {
  const update = useUpdatePayoutDetails();
  const [value, setValue] = useState(current ?? '');
  const [saved, setSaved] = useState(false);

  function save(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    update.mutate(value, { onSuccess: () => setSaved(true) });
  }

  return (
    <form className={styles.card} onSubmit={save}>
      <h2 className={styles.cardTitle}>Where we send your payouts</h2>
      <p className={styles.hint}>
        Groundly pays your balance by hand. Tell us how: a PayPal or Wise email, or bank details.
      </p>
      <textarea
        className={styles.textarea}
        rows={3}
        maxLength={MAX_PAYOUT_DETAILS_LENGTH}
        value={value}
        aria-label="Payout details"
        onChange={(event) => {
          setValue(event.target.value);
          setSaved(false);
        }}
      />
      {update.isError ? (
        <DangerAlert title="Not saved">Check your connection and try again.</DangerAlert>
      ) : null}
      <div className={styles.actions}>
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        {saved ? <span className={styles.muted}>Saved</span> : null}
      </div>
    </form>
  );
}

function ClaimCode() {
  const claim = useClaimReferral();
  const [code, setCode] = useState('');
  const error =
    claim.error instanceof ApiError
      ? claim.error.code === 'NOT_FOUND'
        ? 'No referral code matches that.'
        : claim.error.message
      : claim.isError
        ? 'Check your connection and try again.'
        : undefined;

  return (
    <form
      className={styles.card}
      onSubmit={(event) => {
        event.preventDefault();
        if (code.trim() !== '') claim.mutate(code);
      }}
      noValidate
    >
      <h2 className={styles.cardTitle}>Were you referred?</h2>
      {claim.isSuccess ? (
        <InfoAlert title="Referral code added">
          {`Your next ${claim.data.discountCycles} invoices get ${claim.data.discountPercent}% off.`}
        </InfoAlert>
      ) : (
        <>
          <TextField
            id="referral-code"
            label="Referral code"
            value={code}
            autoCapitalize="characters"
            onChange={(event) => setCode(event.target.value)}
            error={error}
          />
          <div className={styles.actions}>
            <Button type="submit" disabled={claim.isPending}>
              Use code
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
